import axios, { AxiosError, AxiosRequestConfig } from "axios";
import Vue from "vue";
import { ScopeSet, OIDC_DEFAULT_SCOPES } from "@azure/msal-common";
import { MsalPlugin } from "./msalPlugin";
import userInfoService from "@/util/userInfoService";

OIDC_DEFAULT_SCOPES.splice(OIDC_DEFAULT_SCOPES.indexOf("offline_access"), 1);

export interface MicrosoftAuthOptions {
    serverUrl: string,
    msalConfig: any,
    authProviderType?: string
}

export default {
    isAuthenticating: false,
    authenticationPromise: null as Promise<void> | null,
    cachedToken: null as any,
    tokenExpiration: null as Date | null,
    renewalTimer: null as NodeJS.Timeout | null,
    // Token acquisition debouncing
    isFetchingToken: false,
    tokenFetchPromise: null as Promise<any> | null,
    // Store auth provider configuration
    authProviderType: null as string | null,
    msalConfigStored: null as any,
    // Configurable retry parameters
    retryConfig: {
        maxAttempts: 10,
        baseDelay: 1000,
        maxDelay: 30000,
        jitterPercent: 0.3
    },
    async install(app: typeof Vue, options: MicrosoftAuthOptions) {
        // Store configuration for later use
        this.authProviderType = options.authProviderType || 'MSAL';
        this.msalConfigStored = options.msalConfig;

        app.use(MsalPlugin, options.msalConfig);
        await this.setupAuthentication();
    },
    async errorHandler(this: any, err: AxiosError): Promise<any> {
        if (err?.response?.status != 401) return Promise.reject(err);

        console.log("Authentication error detected (401), attempting to re-authenticate...");

        try {
            await this.ensureAuthenticated();
            await this.fetchToken();
            await userInfoService.fetchUserInfo({ forceFresh: true });

            const config = err.response.config;
            
            if (!config.headers) {
                config.headers = {};
            }
            config.headers.Authorization = axios.defaults.headers.Authorization;

            console.log("Re-authentication successful, retrying original request...");
            return new Promise((resolve, reject) => {
                axios(config).then(
                    res => resolve(res),
                    secondErr => {
                        console.error("Retry after re-authentication failed:", secondErr);
                        reject(secondErr);
                    }
                );
            });
        } catch (authError) {
            console.error("Re-authentication failed:", authError);
            // Clear cached token on authentication failure
            this.cachedToken = null;
            this.tokenExpiration = null;
            return Promise.reject(authError);
        }
    },
    async requestHandler(this: any, config: AxiosRequestConfig): Promise<any> {
        // URL normalization
        if (config.url?.startsWith('/api')) {
            config.url = config.url?.substr(4);
        }
        if (config.url?.startsWith('api')) {
            config.url = config.url?.substr(3);
        }

        // Ensure authentication
        await this.ensureAuthenticated();

        try {
            await userInfoService.fetchUserInfoIfNeeded(config.url);
        } catch (error) {
            // Log but don't fail the request - let it proceed and handle auth errors later
            console.warn("Failed to fetch user info in request handler:", error);
        }

        // Check if we need to set/refresh the authorization header
        const needsAuthHeader = !config.headers?.Authorization || 
                               config.headers.Authorization === "Bearer " || 
                               config.headers.Authorization === "Bearer Unknown";

        if (needsAuthHeader) {
            // Check if we have a valid cached token first
            if (!this.isTokenValid()) {
                await this.fetchToken();
            }
            
            // Set authorization header
            if (!config.headers) {
                config.headers = {};
            }
            config.headers.Authorization = axios.defaults.headers.Authorization;
        }
        
        return config;
    },
    async setupAuthentication(this: any): Promise<void> {
        axios.interceptors.response.use(
            response => response,
            (err: AxiosError) => this.errorHandler(err));
        axios.interceptors.request.use(
            (config) => this.requestHandler(config),
            (err) => this.errorHandler(err));
    },
    async fetchToken(forceRefresh: boolean = false): Promise<any> {
        // Return cached token if still valid and not forcing refresh
        if (!forceRefresh && this.isTokenValid()) {
            axios.defaults.headers.Authorization = `BEARER ${this.cachedToken.accessToken}`;
            return this.cachedToken;
        }

        // If token acquisition is already in progress, return the existing promise
        if (this.isFetchingToken && this.tokenFetchPromise) {
            return this.tokenFetchPromise;
        }

        // Start token acquisition process
        this.isFetchingToken = true;
        this.tokenFetchPromise = this.performTokenAcquisition();

        try {
            const token = await this.tokenFetchPromise;
            return token;
        } finally {
            // Clean up token fetch state
            this.isFetchingToken = false;
            this.tokenFetchPromise = null;
        }
    },
    async performTokenAcquisition(): Promise<any> {
        // Determine scopes based on auth provider type
        let scopes: string[];

        if (this.authProviderType === 'OIDC') {
            // Use scopes from OIDC config, fallback to openid
            scopes = this.msalConfigStored?.request?.scopes || ['openid'];
        } else {
            // Standard MSAL - use Azure AD API scope
            scopes = [`api://${Vue.prototype.$config.VITE_APP_ADAL_GRAPHID}/access_as_user`];
        }

        // Acquire new token with appropriate scopes
        const token = await Vue.prototype.$msal.acquireToken(new ScopeSet(scopes));

        // Cache the new token with expiration
        this.cachedToken = token;
        if (token.expiresOn) {
            this.tokenExpiration = new Date(token.expiresOn);
        } else {
            // Fallback: assume 1 hour expiration if not provided
            this.tokenExpiration = new Date(Date.now() + 60 * 60 * 1000);
        }

        // Schedule automatic renewal
        this.scheduleTokenRenewal();

        // console.log(token);
        axios.defaults.headers.Authorization = `BEARER ${token.accessToken}`;

        // Trigger group claims sync after token acquisition
        this.triggerGroupClaimsSync();

        return token;
    },
    async ensureAuthenticated() {
        if (Vue.prototype.$msal.isAuthenticated()) return;
        
        // If authentication is already in progress, return the existing promise
        if (this.isAuthenticating && this.authenticationPromise) {
            return this.authenticationPromise;
        }
        
        // Start authentication process
        this.isAuthenticating = true;
        this.authenticationPromise = this.performAuthentication();
        
        try {
            await this.authenticationPromise;
        } finally {
            // Clean up authentication state
            this.isAuthenticating = false;
            this.authenticationPromise = null;
        }
    },
    async performAuthentication() {
        const { maxAttempts, baseDelay, maxDelay, jitterPercent } = this.retryConfig;

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            if (!this.msalInteractionInProgress()) {
                if (Vue.prototype.$msal.isAuthenticated()) return;
                
                try {
                    await Vue.prototype.$msal.loginPopup();
                    // Trigger group claims sync after successful login
                    this.triggerGroupClaimsSync();
                    return; // Success, exit retry loop
                } catch (error) {
                    console.warn(`Authentication attempt ${attempt + 1} failed:`, error);
                    
                    // Don't retry on the last attempt
                    if (attempt === maxAttempts - 1) {
                        throw new Error(`Authentication failed after ${maxAttempts} attempts: ${error}`);
                    }
                }
            }
            
            // Calculate exponential backoff with jitter
            const exponentialDelay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
            const jitter = Math.random() * jitterPercent * exponentialDelay;
            const delayWithJitter = exponentialDelay + jitter;
            
            console.log(`Waiting ${Math.round(delayWithJitter)}ms before retry attempt ${attempt + 2}`);
            await new Promise(resolve => setTimeout(resolve, delayWithJitter));
        }

        throw new Error("Authentication failed after multiple attempts");
    },
    msalInteractionInProgress(): boolean {
        const progress = !!(Vue.prototype.$msal.instance?.tokenCache?.storage?.getInteractionInProgress() ?? false);
        return progress;
    },
    isTokenValid(): boolean {
        if (!this.cachedToken || !this.tokenExpiration) {
            return false;
        }
        
        // Check if token expires within next 5 minutes (buffer for safety)
        const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
        const now = new Date();
        const expirationWithBuffer = new Date(this.tokenExpiration.getTime() - bufferTime);
        
        return now < expirationWithBuffer;
    },
    scheduleTokenRenewal() {
        // Clear existing timer
        if (this.renewalTimer) {
            clearTimeout(this.renewalTimer);
            this.renewalTimer = null;
        }

        if (!this.tokenExpiration) return;

        // Schedule renewal 10 minutes before expiration (or halfway if token is short-lived)
        const now = new Date();
        const timeUntilExpiration = this.tokenExpiration.getTime() - now.getTime();
        const renewalBuffer = Math.min(10 * 60 * 1000, timeUntilExpiration / 2); // 10 minutes or half the token lifetime
        const renewalTime = Math.max(0, timeUntilExpiration - renewalBuffer);

        if (renewalTime > 0) {
            this.renewalTimer = setTimeout(() => {
                this.silentTokenRenewal();
            }, renewalTime);
        }
    },
    async silentTokenRenewal() {
        try {
            console.log("Attempting silent token renewal...");
            
            // Determine scopes based on auth provider type
            let scopes: string[];
            if (this.authProviderType === 'OIDC') {
                scopes = this.msalConfigStored?.request?.scopes || ['openid'];
            } else {
                scopes = [`api://${Vue.prototype.$config.VITE_APP_ADAL_GRAPHID}/access_as_user`];
            }

            // Try to acquire token silently first
            const token = await Vue.prototype.$msal.acquireTokenSilent(
                ScopeSet.createSearchScopes(scopes)
            );
            
            // Update cached token and expiration
            this.cachedToken = token;
            if (token.expiresOn) {
                this.tokenExpiration = new Date(token.expiresOn);
            } else {
                this.tokenExpiration = new Date(Date.now() + 60 * 60 * 1000);
            }
            
            // Update axios headers
            axios.defaults.headers.Authorization = `BEARER ${token.accessToken}`;
            
            // Schedule next renewal
            this.scheduleTokenRenewal();
            
            // Trigger group claims sync after token renewal
            this.triggerGroupClaimsSync();
            
            console.log("Silent token renewal successful");
        } catch (error) {
            console.warn("Silent token renewal failed, will attempt interactive renewal on next request:", error);
            // Clear cached token to force interactive renewal on next request
            this.cachedToken = null;
            this.tokenExpiration = null;
        }
    },
    async triggerGroupClaimsSync() {
        try {
            console.log("Triggering group claims sync...");
            const response = await axios.post("/User/sync-group-claims");
            console.log("Group claims sync response:", response.data);
            return response.data;
        } catch (error) {
            console.warn("Group claims sync failed:", error);
            // Don't throw - this shouldn't block authentication
            return { success: false, error };
        }
    }
}
